/**
 * CharDetailsPanel - Unified character panel
 *
 * Combines vitals (HP, stats, conditions) and loadout (portrait, equipment,
 * spells, features) into a single scrollable panel. Used in both lobby and
 * game-session contexts.
 *
 * Sections (accordion):
 * 1. Vitals - HP, XP, ability scores, combat stats, conditions
 * 2. Portrait - AI-generated character portrait (image or video)
 * 3. Equipped Items - Currently equipped weapons, armor, shield
 * 4. Actions - Clickable spell, item, and ability shortcuts in game sessions
 * 5. Prepared Spells - Spell slots and prepared spells
 * 6. Features & Traits - Class features, racial traits, feats
 * 7. Clothing / Appearance - What the character is wearing
 * 8. All Possessions - All equipment items
 * 9. Spellbook - All spells known by level
 */

import { useState, useMemo } from 'react';
import { useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { useGameStore } from '@/lib/stores/gameStore';
import {
  EquippedSection,
  FeaturesSection,
  HeaderCard,
  InventorySection,
  PortraitSection,
  PreparedSection,
  SpellSlotsSection,
  SpellbookSection,
} from './CharDetailsSections';
import type {
  EquippedSlotsData,
  SlotItem,
  InventoryDisplayItem,
  SpellbookLevel,
} from './CharDetailsSections';
import { preparationCap } from './CharDetailsSections.helpers';
import { VitalsSection } from './VitalsSection';
import {
  ActionsSection,
  type ActionSpell,
  type ActionWeapon,
  type ActionItem,
  type ActionAbility,
  type ActiveState,
} from './ActionsSection';
import { getNumberedSpells, buildFreeCastContext, mergeSpellbookLevels } from './game-commands';
import { formatDiceFormula } from '@danda/shared';
import type { DiceFormula } from '@danda/shared';
import { computeLobbyVitals, computeGameVitals } from './vitals-utils';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import { usePortraitRequest } from '@/lib/hooks/usePortraitRequest';
import { actionTriggerRef } from '@/lib/action-trigger';
import { gameConnectionManager } from '@/lib/game-connection-manager';
import { resolveCardUses as resolveCardUsesFor, type CardUsesContext } from './card-uses';

/** Types that represent unique objects — never group even if same name. */
const NON_STACKABLE_TYPES = new Set(['armor', 'wondrous_item', 'clothing', 'tool']);

// (Legacy `SELF_ONLY_ABILITY_NAMES` fallback was removed when the Actions loop
// switched to reading from `ability_mechanics` — target_type is now derived
// from the effect variant via `deriveAbilityTargetType()` in game-commands.ts.)

/**
 * Group stackable same-name inventory items for display.
 * Non-stackable types always stay as separate entries.
 * Equipped items are keyed separately from unequipped.
 */
function groupInventoryItems(items: InventoryDisplayItem[]): InventoryDisplayItem[] {
  const grouped = new Map<string, InventoryDisplayItem>();
  const result: InventoryDisplayItem[] = [];

  for (const item of items) {
    if (NON_STACKABLE_TYPES.has(item.type)) {
      result.push(item);
      continue;
    }

    const key = `${item.name.toLowerCase()}|${item.type}|${item.equipped}|${item.bonus ?? ''}|${item.damage ?? ''}|${item.ac ?? ''}|${item.is_magic ? '1' : '0'}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.quantity += item.quantity ?? 1;
    } else {
      const clone = { ...item };
      grouped.set(key, clone);
      result.push(clone);
    }
  }

  return result;
}

interface CharDetailsPanelProps {
  /** Context: 'lobby' for full details, 'game-session' for simplified view */
  context?: 'lobby' | 'game-session';
}

export default function CharDetailsPanel({ context = 'lobby' }: CharDetailsPanelProps) {
  // Lobby context data
  const selectedCharacter = useSelectedCharacter();

  // Game session context data — read from partyMembersMap (updated by round_result)
  const { playerCharacterId, partyMembersMap, encounterType } = useGameStore();
  const gameCharacter = useMemo(() => {
    if (context !== 'game-session' || !playerCharacterId) return null;
    return partyMembersMap[playerCharacterId] ?? null;
  }, [context, playerCharacterId, partyMembersMap]);

  const [expandedSection, setExpandedSection] = useState<string | null>(
    context === 'game-session' ? 'actions' : 'vitals'
  );
  const [isExpanded, toggleExpanded] = usePanelCollapse('loadout');

  // Portrait request logic (lobby only — guarded inside hook)
  const { effectivePortraitUrl, characterPortraitType, isGeneratingPortrait } =
    usePortraitRequest(context);

  // Compute vitals for current context
  const lobbyVitals = useMemo(
    () => (context === 'lobby' ? computeLobbyVitals(selectedCharacter) : null),
    [context, selectedCharacter]
  );
  const gameVitals = useMemo(
    () => (context === 'game-session' ? computeGameVitals(gameCharacter) : null),
    [context, gameCharacter]
  );
  const vitalsData = lobbyVitals ?? gameVitals;

  // ============================================
  // GAME SESSION CONTEXT
  // ============================================
  if (context === 'game-session') {
    if (!gameCharacter || !gameCharacter.permanent) {
      return null;
    }

    const permanent = gameCharacter.permanent;
    const session = gameCharacter.session;

    // Build slot-based equipped data + inventory list
    const inventory = permanent?.inventory ?? [];
    // Build emoji lookup from all mechanics sources
    const itemEmojiMap = new Map<string, string>();
    for (const w of (permanent?.weapon_mechanics as Array<Record<string, unknown>>) ?? []) {
      if (w.emoji && w.name) itemEmojiMap.set((w.name as string).toLowerCase(), w.emoji as string);
    }
    for (const m of (permanent?.magic_item_mechanics as Array<Record<string, unknown>>) ?? []) {
      if (m.emoji && m.name) itemEmojiMap.set((m.name as string).toLowerCase(), m.emoji as string);
    }
    for (const g of (permanent?.gear_mechanics as Array<Record<string, unknown>>) ?? []) {
      if (g.emoji && g.name) itemEmojiMap.set((g.name as string).toLowerCase(), g.emoji as string);
    }
    // Char-creation Items don't populate Item.damage; weapon_mechanics is the
    // authoritative source for both intrinsic and looted weapons.
    // Keyed by lowercased name — assumes weapon names are unique per character.
    // Identical-name dupes (e.g. 3 mundane daggers) collide harmlessly because
    // their damage_dice are identical. Two distinct items with the same display
    // name (e.g. mundane and magical "Dagger") would last-write-wins; current
    // naming convention ("Dagger" vs "Dagger +1") avoids this.
    const weaponDamageMap = new Map<string, string>();
    for (const w of (permanent?.weapon_mechanics as Array<Record<string, unknown>>) ?? []) {
      const dice = w.damage_dice as DiceFormula | undefined;
      if (w.name && dice?.count && dice?.sides) {
        weaponDamageMap.set((w.name as string).toLowerCase(), formatDiceFormula(dice));
      }
    }

    const findItem = (id?: string): SlotItem | undefined => {
      if (!id) return undefined;
      const item = inventory.find((i) => i.item_id === id);
      if (!item) return undefined;
      return {
        name: item.name,
        emoji: itemEmojiMap.get(item.name.toLowerCase()),
        type: item.item_kind as string,
        bonus: item.attack_bonus ? `+${item.attack_bonus}` : undefined,
        damage: weaponDamageMap.get(item.name.toLowerCase()) ?? item.damage,
        ac: item.base_ac ?? item.ac_bonus,
        properties: item.properties ?? [],
      };
    };

    const equippedSlots = session?.equipped_slots;
    const equippedItemIds: string[] = equippedSlots
      ? [
          equippedSlots.main_hand,
          equippedSlots.off_hand,
          equippedSlots.armor,
          ...(equippedSlots.wondrous_items ?? []),
        ].filter((v): v is string => typeof v === 'string')
      : [];

    const gameSlotsData: EquippedSlotsData = {
      mainHand: findItem(equippedSlots?.main_hand),
      offHand: findItem(equippedSlots?.off_hand),
      armor: findItem(equippedSlots?.armor),
      wondrousItems: (equippedSlots?.wondrous_items ?? [])
        .map(findItem)
        .filter((v): v is SlotItem => v != null),
      twoHandedEquipped: equippedSlots?.two_handed_equipped ?? false,
    };

    const gameInventory: InventoryDisplayItem[] = groupInventoryItems(
      inventory.map((item) => ({
        name: item.name,
        emoji: itemEmojiMap.get(item.name.toLowerCase()),
        type: item.item_kind as string,
        quantity: item.quantity,
        equipped: equippedItemIds.some((id) => id === item.item_id),
        bonus: item.attack_bonus ? `+${item.attack_bonus}` : undefined,
        damage: weaponDamageMap.get(item.name.toLowerCase()) ?? item.damage,
        ac: item.base_ac ?? item.ac_bonus,
        description: item.description,
        is_magic:
          (item.attack_bonus ?? 0) > 0 ||
          (item.ac_bonus ?? 0) > 0 ||
          item.attunement === true ||
          (item.item_kind as string) === 'rare',
      }))
    );

    // Spellcasting from permanent, slots used from session.
    // Spell levels use D&D convention: 0 = cantrip, 1-9 = leveled spell.
    // Backend always sends SpellData objects with base_level; string fallback is defensive.
    const spellcasting = permanent?.spellcasting as Record<string, unknown> | undefined;
    const rawPrepared = (session?.prepared_spells as Array<unknown>) ?? [];
    const preparedFromSession = rawPrepared
      .map((s) => {
        if (typeof s === 'string') return { name: s, level: 0 };
        const obj = s as Record<string, unknown>;
        return { name: (obj?.name as string) ?? '', level: (obj?.base_level as number) ?? 0 };
      })
      .filter((s) => s.name);
    const rawAlwaysPrepared = (spellcasting?.always_prepared as Array<unknown>) ?? [];
    const alwaysPreparedEntries = rawAlwaysPrepared
      .map((s) => {
        if (typeof s === 'string') return { name: s, level: 0 };
        const obj = s as Record<string, unknown>;
        return { name: (obj?.name as string) ?? '', level: (obj?.base_level as number) ?? 0 };
      })
      .filter((s) => s.name);
    // Union session-prepared + always-prepared for display, deduped by lowercase
    // name. Always-prepared (feat / domain / subclass grants) must stay visible
    // even when the player has session-prepared spells — Cure Wounds from Magic
    // Initiate, Cleric domain spells, Paladin oath spells. Per SRD 5.2.1 these
    // do NOT count against the prep cap; that's handled below via
    // `chosenPreparedCountGame`. Matches the lobby branch + `mergeSpellbookLevels`.
    // Known casters (Bard/Sorcerer/Warlock) don't carry `always_prepared` so the
    // union is a no-op for them.
    const preparedSpellEntries = (() => {
      const seen = new Set<string>();
      const out: { name: string; level: number }[] = [];
      for (const entry of [...preparedFromSession, ...alwaysPreparedEntries]) {
        const key = entry.name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(entry);
      }
      return out;
    })();
    // Cap count excludes always-prepared overlap. For chars whose session list
    // already excludes always-prepared (post-2026-05-24 fix in
    // `build_prepared_spells_from_llm`) this is just `preparedFromSession.length`;
    // for legacy chars whose session list still contains always-prepared, the
    // filter drops those entries so the cap still reads correctly.
    const alwaysPreparedLowerGame = new Set(alwaysPreparedEntries.map((e) => e.name.toLowerCase()));
    const chosenPreparedCountGame = preparedFromSession.filter(
      (e) => !alwaysPreparedLowerGame.has(e.name.toLowerCase())
    ).length;
    const gameSpellMechanics = (permanent?.spell_mechanics as Array<Record<string, unknown>>) ?? [];
    const preparedEmojiMap = new Map(
      gameSpellMechanics
        .filter((s) => s.emoji && s.name)
        .map((s) => [(s.name as string).toLowerCase(), s.emoji as string])
    );
    const preparedSummaryMap = new Map(
      gameSpellMechanics
        .filter((s) => s.summary && s.name)
        .map((s) => [(s.name as string).toLowerCase(), s.summary as string])
    );
    const preparedSpellNames = preparedSpellEntries.map((s) => {
      const emoji = preparedEmojiMap.get(s.name.toLowerCase());
      const summary = preparedSummaryMap.get(s.name.toLowerCase());
      return emoji || summary ? { name: s.name, emoji, summary } : s.name;
    });
    // Use gameStore's spellSlotsUsed (updated by round_result) for consistency
    // with the Actions section which also reads from gameStore
    const spellSlotsUsed = gameCharacter.spellSlotsUsed ?? {};
    const gameSpellSlots: Array<{ level: number; available: number; max: number }> = [];
    if (spellcasting?.max_spell_slots) {
      for (const [level, max] of Object.entries(
        spellcasting.max_spell_slots as Record<string, number>
      )) {
        if (max && max > 0) {
          const levelNum = parseInt(level);
          const used = spellSlotsUsed[levelNum] ?? 0;
          gameSpellSlots.push({ level: levelNum, available: max - used, max });
        }
      }
    }

    // Feats from permanent
    const rawFeats =
      (permanent?.feats as Array<string | { name: string; description?: string }>) ?? [];
    const gameFeats: Array<{ name: string; emoji?: string; description?: string }> = rawFeats.map(
      (f) => (typeof f === 'string' ? { name: f } : f)
    );

    // Features from permanent
    const classFeatures = (permanent?.class_features as Array<Record<string, unknown>>) ?? [];
    const traitsArray =
      permanent?.traits &&
      (permanent.traits as unknown as Array<{ name: string; description?: string }>).length > 0
        ? (permanent.traits as unknown as Array<{ name: string; description?: string }>)
        : (permanent?.species_traits ?? []);
    const gameFeatures = [
      ...classFeatures.map((f) => ({
        name: (f.name as string) ?? '',
        emoji: f.emoji as string | undefined,
        source: (permanent?.class as string) ?? 'Class',
        uses: f.max_uses
          ? `${f.max_uses} / ${((f.recharge as string) ?? 'rest').replace(/_/g, ' ')}`
          : 'Passive',
        description: f.description as string | undefined,
      })),
      ...traitsArray.map((t) => ({
        name: t.name ?? '',
        emoji: (t as Record<string, unknown>).emoji as string | undefined,
        source: (permanent?.species as string) ?? 'Racial',
        uses: 'Passive',
        description: t.description,
      })),
    ];

    // Spellbook from permanent. `mergeSpellbookLevels` folds `always_prepared`
    // (feat / subclass / domain grants like Magic Initiate Cure Wounds) into
    // the known-by-level list with case-insensitive dedupe — Hunter's Mark
    // appears in BOTH lists for Rangers and would otherwise render twice.
    const gameAllKnownSpells: SpellbookLevel[] = mergeSpellbookLevels(
      permanent?.spellcasting,
      permanent?.spell_mechanics
    );

    // Build free-cast context from session data so innate spells (species / Magic
    // Initiate) render with the correct `1/1` / `0/1` quota and disable when
    // exhausted. Long rest clears both feature_uses and class_resources server-side
    // (rest/long_rest.rs:103-104), so the next syncPlayerCharData frame after a
    // rest shows fresh quotas without any frontend state reset.
    const freeCastContext = buildFreeCastContext(permanent, session);

    // Actions: castable spells
    const actionSpells: ActionSpell[] = getNumberedSpells(
      permanent.spellcasting,
      gameCharacter.spellSlotsUsed,
      permanent.spell_mechanics,
      preparedSpellEntries.length > 0 ? preparedSpellEntries : undefined,
      freeCastContext
    );

    // Actions: weapon attacks for any inventory weapon with enriched combat mechanics
    const actionWeapons: ActionWeapon[] = (() => {
      const seen = new Set<string>();
      return inventory
        .filter((item) => weaponDamageMap.has(item.name.toLowerCase()))
        .filter((item) => {
          const key = item.name.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((item) => {
          const key = item.name.toLowerCase();
          return {
            name: item.name,
            emoji: itemEmojiMap.get(key),
            bonus: item.attack_bonus ? `+${item.attack_bonus}` : undefined,
            damage: weaponDamageMap.get(key) ?? item.damage,
          };
        });
    })();

    // Actions: equipped activatable magic items
    const actionItems: ActionItem[] = (() => {
      const mechanics = (permanent?.magic_item_mechanics as Array<Record<string, unknown>>) ?? [];
      if (mechanics.length === 0) return [];
      const equippedIds = new Set<string>();
      if (equippedSlots) {
        if (equippedSlots.main_hand) equippedIds.add(equippedSlots.main_hand);
        if (equippedSlots.off_hand) equippedIds.add(equippedSlots.off_hand);
        if (equippedSlots.armor) equippedIds.add(equippedSlots.armor);
        for (const id of equippedSlots.wondrous_items ?? []) equippedIds.add(id);
      }
      return mechanics
        .filter((m) => m.activation !== 'none' && m.item_id && equippedIds.has(m.item_id as string))
        .map((m) => {
          const effect = m.effect as Record<string, unknown> | undefined;
          const effectType = (effect?.type as string) ?? '';
          // Map serde internally tagged effect types to our categories
          let mappedEffectType: ActionItem['effectType'];
          let spells: ActionItem['spells'];
          let abilities: ActionItem['abilities'];

          switch (effectType) {
            case 'cast_spell_charges': {
              mappedEffectType = 'cast_spell_charges';
              const rawSpells = (effect?.spells as Array<Record<string, unknown>>) ?? [];
              spells = rawSpells.map((s) => ({
                spellName: (s.spell_name as string) ?? '',
                charges: (s.charges as number) ?? 1,
                target_audience: s.target_audience as string | undefined,
              }));
              break;
            }
            case 'special_abilities': {
              mappedEffectType = 'special_abilities';
              const rawAbilities = (effect?.abilities as Array<Record<string, unknown>>) ?? [];
              abilities = rawAbilities.map((a) => ({
                name: (a.name as string) ?? '',
                trigger: (a.trigger as string) ?? 'on_use',
                charges: a.charges as number | undefined,
              }));
              break;
            }
            case 'cast_spell':
              mappedEffectType = 'cast_spell';
              break;
            case 'detection':
            case 'summon_creature':
              mappedEffectType = 'self';
              break;
            default:
              mappedEffectType = 'targeted';
              break;
          }

          return {
            name: (m.name as string) ?? '',
            emoji: m.emoji as string | undefined,
            charges: m.charges as number | undefined,
            isDepleted: typeof m.charges === 'number' && m.charges <= 0,
            itemId: m.item_id as string | undefined,
            effectType: mappedEffectType,
            spells,
            abilities,
          };
        })
        .filter((m) => m.name)
        .filter((m) => {
          // Exclude special_abilities items with no on_use abilities (passive/on_hit only)
          if (m.effectType === 'special_abilities') {
            return m.abilities?.some((a) => a.trigger === 'on_use') ?? false;
          }
          return true;
        });
    })();

    // Actions: class + species abilities from ability_mechanics (single source of truth).
    //
    // ability_mechanics[] is the canonical computed model (AbilityData with effect,
    // resource_cost, target rules). class_features[] is consulted ONLY for the
    // `rest_only` flag (a text-meta concern), not for max_uses or target_type.
    //
    // Max/remaining per ability is derived from the resource_type on the ability's
    // resource_cost. That resolution now lives in `./card-uses`, which is where a
    // new server-side resource type needs its branch.
    const featureUsesMap = gameCharacter.featureUses ?? {};
    const classResources = (session?.class_resources as Record<string, unknown> | undefined) ?? {};
    const classAbilities =
      (permanent?.class_abilities as Record<string, unknown> | undefined) ?? {};

    // Action-card resource resolution lives in `./card-uses` so it can be tested
    // without mounting this panel (same reason `mergeSpellbookLevels` sits in
    // `./game-commands`). Bind the character state it reads once, here.
    const cardUsesCtx: CardUsesContext = {
      classAbilities,
      classResources,
      featureUses: featureUsesMap,
      level: gameCharacter.level,
      computed: permanent?.computed as Record<string, unknown> | undefined,
    };
    const resolveCardUses = (resource: string, cardLabel: string, requirementAmount?: number) =>
      resolveCardUsesFor(cardUsesCtx, resource, cardLabel, requirementAmount);

    const actionAbilities: ActionAbility[] = [];

    // Build a lookup of class_features by lowercased name for rest_only meta.
    const classFeaturesByName = new Map<string, Record<string, unknown>>();
    for (const f of classFeatures) {
      const n = ((f.name as string) ?? '').toLowerCase();
      if (n) classFeaturesByName.set(n, f);
    }

    // ActionCard-driven abilities list (single source of truth). Reads
    // `permanent.action_cards`; passive phantoms are already filtered at
    // enrichment time via PASSIVE_EXCLUSIONS + OnHit trigger filter.
    //
    // We keep a parallel `actionCardsByLabel` lookup so the click handler
    // can dispatch via `triggerCardShortcut(card)` — which builds the
    // correct hint (flurry_of_blows, mode_id, metamagic, activate) from the
    // card's activation kind. See docs/game-engine/ACTION_CARDS.md.
    const actionCards = permanent?.action_cards ?? [];
    const actionCardsByLabel = new Map<string, (typeof actionCards)[number]>();
    for (const card of actionCards) {
      // Skip cards that have their own panels (spells, items, base actions).
      // Class-ability first-class intents (Rogue Steady Aim, etc.) DO surface
      // here — only base-action first-class intents are covered by the
      // static `/dodge` commands.
      if (card.source.kind === 'base_action') continue;
      if (card.activation.kind === 'use_item') continue;
      if (card.activation.kind === 'cast_spell_from_source') continue;

      // Reaction-cost cards (Parry, Riposte, Uncanny Dodge, Deflect,
      // etc.) auto-fire via the engine's reaction subscription system
      // on their natural trigger — there's no proactive activation
      // path, so rendering them as clickable Actions-panel buttons
      // just leads to engine rejections. Mirrors the slash-menu filter
      // in `getAbilityCommands` and the mobile chat panel.
      if (card.availability.action_cost === 'reaction') continue;

      // Suppress point-target cards (Dragonborn Breath Weapon, area-form
      // Turn Undead, Land's Aid, etc.) until a real point/area selection
      // flow exists. Without structured point data the backend handlers
      // fall back to targeting every opposing combatant, so a cone-shaped
      // Breath Weapon would hit every enemy on the battlefield. Players
      // can still invoke these via natural-language chat — the LLM
      // populates specific target IDs from the scene.
      if (card.target_picker?.kind === 'point') continue;

      actionCardsByLabel.set(card.label.toLowerCase(), card);

      // rest_only comes from class_features meta (text-only source).
      const matchingFeature = classFeaturesByName.get(card.label.toLowerCase());
      if (matchingFeature?.rest_only && encounterType === 'combat') continue;

      // Map card resource requirement to remaining/max uses for the old
      // `ActionAbility` shape. Cards without a resource_requirement render
      // with max=1 remaining=1 (always available until backend rejects).
      const req = card.availability.resource_requirement;
      let remaining = 1;
      let max = 1;
      if (req) {
        // For common resources we can derive the remaining/max via the
        // same resolver used by the old ability_mechanics loop; for
        // metamagic / feat / toggle cards we fall back to 1/1.
        const resolved = resolveCardUses(req.resource, card.label, req.amount);
        if (resolved) {
          remaining = resolved.remaining;
          max = resolved.max;
        }
      }

      // Target-picker mapping → old self_only / targeted flag for backward
      // compat with ActionsSection's button styling. Multi-mode cards are
      // surfaced as targeted to hint at the modal-picker step (which
      // currently auto-picks the first mode).
      const picker = card.target_picker?.kind;
      const targetType: 'self_only' | 'targeted' =
        picker == null || picker === 'self' || picker === 'none' ? 'self_only' : 'targeted';

      actionAbilities.push({
        name: card.label,
        emoji: card.emoji,
        description: card.description,
        remainingUses: remaining,
        maxUses: max,
        recharge: (matchingFeature?.recharge as string) ?? 'long_rest',
        targetType,
      });
    }

    // Actions: active states (concentration) — friendly-gated server-side.
    // Self-view always carries the spell name; non-friendly observers see
    // concentrationSpell=null and the label drops to bare "CONCENTRATING".
    const actionActiveStates: ActiveState[] = [];
    if (gameCharacter.isConcentrating) {
      const label = gameCharacter.concentrationSpell
        ? `CONCENTRATING: ${gameCharacter.concentrationSpell.toUpperCase()}`
        : 'CONCENTRATING';
      actionActiveStates.push({
        type: 'concentration',
        label,
      });
    }

    const gameHasActions =
      actionSpells.some((s) => {
        if (s.isCantrip) return true;
        if (s.freeCastable) {
          return s.freeCastRemaining == null || s.freeCastRemaining > 0;
        }
        return (s.slotsRemaining ?? 0) > 0;
      }) ||
      actionWeapons.length > 0 ||
      actionItems.some((item) => !item.isDepleted) ||
      actionAbilities.some((a) => a.remainingUses > 0) ||
      actionActiveStates.length > 0;

    // Action callbacks (plain functions — these reference module-level singletons, no state deps)
    const handleSpellClick = (spellName: string, spellLevel: number, freeCast?: boolean) => {
      actionTriggerRef.current?.triggerSpellShortcut(spellName, spellLevel, freeCast);
    };

    const handleWeaponAttackClick = (weaponName: string) => {
      actionTriggerRef.current?.triggerWeaponAttackShortcut(weaponName);
    };

    const handleAbilityClick = (abilityName: string, targetType?: 'self_only' | 'targeted') => {
      // Prefer the card-aware dispatcher when an ActionCard exists for this
      // ability. The card carries the correct activation kind (Attack-with-
      // Flurry, multi-mode, toggle, etc.) and produces the right typed
      // Intent via lib/intent/build-from-card.ts. Fallback to the legacy
      // name-based trigger preserves compatibility for characters whose
      // enricher hasn't yet populated action_cards (pre-refactor sessions).
      const card = actionCardsByLabel.get(abilityName.toLowerCase());
      if (card) {
        // Pool-spend cards (Paladin Lay on Hands) need the current pool
        // remaining so the amount picker can cap options. The resolver
        // returns remaining for the card's resource_requirement.
        const req = card.availability.resource_requirement;
        const runtime =
          req?.resource === 'lay_on_hands_pool'
            ? { poolRemaining: resolveCardUses(req.resource, card.label)?.remaining ?? 0 }
            : undefined;
        actionTriggerRef.current?.triggerCardShortcut(card, runtime);
      } else {
        // #275 (2026-05-10): legacy fallback path. When action_cards isn't
        // populated yet (post-session-restore before re-enrichment), pool
        // abilities like Lay on Hands need pool-remaining injected via
        // `runtime.poolRemaining` so the picker can render the correct max.
        // Compute from class_abilities + class_resources directly (same
        // source as resolveCardUses for lay_on_hands_pool).
        const isLayOnHands =
          abilityName.toLowerCase().replace(/[^a-z0-9]+/g, '_') === 'lay_on_hands';
        const runtime = isLayOnHands
          ? {
              poolRemaining: resolveCardUses('lay_on_hands_pool', abilityName)?.remaining ?? 0,
            }
          : undefined;
        actionTriggerRef.current?.triggerAbilityShortcut(abilityName, targetType, runtime);
      }
    };

    const handleItemClick = (item: ActionItem) => {
      actionTriggerRef.current?.triggerItemShortcut({
        itemName: item.name,
        itemId: item.itemId,
        effectType: item.effectType ?? 'self',
        spells: item.spells,
        abilities: item.abilities,
        charges: item.charges,
      });
    };

    const handleEndState = (_state: ActiveState) => {
      // Concentration drop has a clean typed Intent — ship `player_intent` so
      // the backend skips the LLM agent. End-concentration is a free action
      // (no action-economy cost), so action_slot is `'free'`.
      gameConnectionManager.sendAction({
        type: 'player_intent',
        intent: { intent_type: 'end_concentration' },
        action_slot: 'free',
        scene_id_at_emit: useGameStore.getState().currentScene?.sceneId,
      });
    };

    // Section visibility
    const gameHasVitals = vitalsData != null;
    const gamePortraitUrl = gameCharacter.portraitUrl || (session?.portrait_url as string) || null;
    const gameHasPortrait = !!gamePortraitUrl;
    const gameHasEquippedSlots = true;
    const gameHasPreparedContent =
      preparedSpellNames.length > 0 ||
      preparationCap(
        permanent?.class as string | undefined,
        spellcasting?.max_prepared_spells as number | undefined
      ) != null;
    const gameHasFeaturesOrFeats = gameFeatures.length > 0 || gameFeats.length > 0;
    const gameHasInventory = gameInventory.length > 0 || ((permanent?.gold_cp as number) ?? 0) > 0;
    const gameHasSpellbook = gameAllKnownSpells.length > 0;

    const gameSections = [
      { key: 'vitals', hasContent: gameHasVitals },
      { key: 'actions', hasContent: gameHasActions },
      { key: 'portrait', hasContent: gameHasPortrait },
      { key: 'equipped', hasContent: gameHasEquippedSlots },
      { key: 'spellslots', hasContent: gameSpellSlots.length > 0 },
      { key: 'prepared', hasContent: gameHasPreparedContent },
      { key: 'spellbook', hasContent: gameHasSpellbook },
      { key: 'features', hasContent: gameHasFeaturesOrFeats },
      { key: 'inventory', hasContent: gameHasInventory },
    ];
    const gameVisibleSections = gameSections.filter((s) => s.hasContent);
    const gameNeedsSeparator = (key: string) => {
      const idx = gameVisibleSections.findIndex((s) => s.key === key);
      return idx < gameVisibleSections.length - 1;
    };

    const gameToggleSection = (section: string) => {
      setExpandedSection(expandedSection === section ? null : section);
    };

    const gameAvatarEmoji = gameCharacter.avatarEmoji ?? (permanent?.avatar_emoji as string);
    const gameSubtitle =
      `Level ${gameCharacter.level ?? 1} ${gameCharacter.species ?? ''} ${gameCharacter.class ?? ''}`.trim();

    return (
      <div
        className={
          isExpanded
            ? 'h-full flex flex-col cursor-default [&_button]:cursor-pointer'
            : 'flex flex-col self-start w-full cursor-default [&_button]:cursor-pointer'
        }
      >
        <HeaderCard
          characterName={gameCharacter.name}
          subtitle={gameSubtitle}
          avatar={gameCharacter.avatarUrl}
          avatarEmoji={gameAvatarEmoji}
          portraitUrl={gameCharacter.portraitUrl}
          isExpanded={isExpanded}
          onToggleExpanded={toggleExpanded}
        >
          {isExpanded && (
            <>
              {vitalsData && (
                <VitalsSection
                  data={vitalsData}
                  expanded={expandedSection === 'vitals'}
                  onToggle={() => gameToggleSection('vitals')}
                  showSeparator={gameNeedsSeparator('vitals')}
                />
              )}

              {gameHasActions && (
                <ActionsSection
                  expanded={expandedSection === 'actions'}
                  onToggle={() => gameToggleSection('actions')}
                  showSeparator={gameNeedsSeparator('actions')}
                  spells={actionSpells}
                  weapons={actionWeapons}
                  items={actionItems}
                  abilities={actionAbilities}
                  activeStates={actionActiveStates}
                  onSpellClick={handleSpellClick}
                  onWeaponAttackClick={handleWeaponAttackClick}
                  onAbilityClick={handleAbilityClick}
                  onItemClick={handleItemClick}
                  onEndState={handleEndState}
                  disabled={
                    gameCharacter.healthState === 'dying' || gameCharacter.healthState === 'dead'
                  }
                />
              )}

              {gameHasPortrait && (
                <PortraitSection
                  portraitUrl={gamePortraitUrl}
                  portraitType="image"
                  characterName={gameCharacter.name}
                  expanded={expandedSection === 'portrait'}
                  onToggle={() => gameToggleSection('portrait')}
                  showSeparator={gameNeedsSeparator('portrait')}
                  isGenerating={false}
                />
              )}

              {gameHasEquippedSlots && (
                <EquippedSection
                  slots={gameSlotsData}
                  expanded={expandedSection === 'equipped'}
                  onToggle={() => gameToggleSection('equipped')}
                  showSeparator={gameNeedsSeparator('equipped')}
                />
              )}

              {gameSpellSlots.length > 0 && (
                <SpellSlotsSection
                  expanded={expandedSection === 'spellslots'}
                  onToggle={() => gameToggleSection('spellslots')}
                  spellSlots={gameSpellSlots}
                  showSeparator={gameNeedsSeparator('spellslots')}
                />
              )}

              {gameHasPreparedContent && (
                <PreparedSection
                  expanded={expandedSection === 'prepared'}
                  onToggle={() => gameToggleSection('prepared')}
                  preparedSpellNames={preparedSpellNames}
                  chosenPreparedCount={chosenPreparedCountGame}
                  maxPreparedSpells={preparationCap(
                    permanent?.class as string | undefined,
                    spellcasting?.max_prepared_spells as number | undefined
                  )}
                  showSeparator={gameNeedsSeparator('prepared')}
                />
              )}

              {gameHasSpellbook && (
                <SpellbookSection
                  expanded={expandedSection === 'spellbook'}
                  onToggle={() => gameToggleSection('spellbook')}
                  allKnownSpells={gameAllKnownSpells}
                  showSeparator={gameNeedsSeparator('spellbook')}
                />
              )}

              {gameHasFeaturesOrFeats && (
                <FeaturesSection
                  expanded={expandedSection === 'features'}
                  onToggle={() => gameToggleSection('features')}
                  feats={gameFeats}
                  features={gameFeatures}
                  showSeparator={gameNeedsSeparator('features')}
                />
              )}

              {gameHasInventory && (
                <InventorySection
                  expanded={expandedSection === 'inventory'}
                  onToggle={() => gameToggleSection('inventory')}
                  items={gameInventory}
                  goldCp={
                    ((permanent?.gold_cp as number) ?? 0) > 0
                      ? (permanent!.gold_cp as number)
                      : undefined
                  }
                  showSeparator={gameNeedsSeparator('inventory')}
                />
              )}
            </>
          )}
        </HeaderCard>
      </div>
    );
  }

  // ============================================
  // LOBBY CONTEXT - Full detailed view
  // ============================================

  if (!selectedCharacter) {
    return null;
  }

  const characterName = selectedCharacter.permanent?.name ?? 'Character';
  const permanent = selectedCharacter.permanent;
  const session = selectedCharacter.session;

  // Build slot-based equipped data + inventory list
  const lobbyInventory = permanent?.inventory ?? [];

  // Build emoji lookup from all mechanics sources (mirrors game context)
  const lobbyItemEmojiMap = new Map<string, string>();
  for (const w of (permanent?.weapon_mechanics as Array<Record<string, unknown>>) ?? []) {
    if (w.emoji && w.name)
      lobbyItemEmojiMap.set((w.name as string).toLowerCase(), w.emoji as string);
  }
  for (const m of (permanent?.magic_item_mechanics as Array<Record<string, unknown>>) ?? []) {
    if (m.emoji && m.name)
      lobbyItemEmojiMap.set((m.name as string).toLowerCase(), m.emoji as string);
  }
  for (const g of (permanent?.gear_mechanics as Array<Record<string, unknown>>) ?? []) {
    if (g.emoji && g.name)
      lobbyItemEmojiMap.set((g.name as string).toLowerCase(), g.emoji as string);
  }
  // Lobby-branch counterpart of `weaponDamageMap` above — same name-uniqueness
  // assumption applies (see comment at the game-branch construction site).
  const lobbyWeaponDamageMap = new Map<string, string>();
  for (const w of (permanent?.weapon_mechanics as Array<Record<string, unknown>>) ?? []) {
    const dice = w.damage_dice as DiceFormula | undefined;
    if (w.name && dice?.count && dice?.sides) {
      lobbyWeaponDamageMap.set((w.name as string).toLowerCase(), formatDiceFormula(dice));
    }
  }

  const lobbyFindItem = (id?: string): SlotItem | undefined => {
    if (!id) return undefined;
    const item = lobbyInventory.find((i) => i.item_id === id);
    if (!item) return undefined;
    return {
      name: item.name,
      emoji: lobbyItemEmojiMap.get(item.name.toLowerCase()),
      type: item.item_kind as string,
      bonus: item.attack_bonus ? `+${item.attack_bonus}` : undefined,
      damage: lobbyWeaponDamageMap.get(item.name.toLowerCase()) ?? item.damage,
      ac: item.base_ac ?? item.ac_bonus,
      properties: item.properties ?? [],
    };
  };

  const lobbyEquippedSlots = session?.equipped_slots;
  const lobbyEquippedItemIds: string[] = lobbyEquippedSlots
    ? [
        lobbyEquippedSlots.main_hand,
        lobbyEquippedSlots.off_hand,
        lobbyEquippedSlots.armor,
        ...(lobbyEquippedSlots.wondrous_items ?? []),
      ].filter((v): v is string => typeof v === 'string')
    : [];

  const lobbySlotsData: EquippedSlotsData = {
    mainHand: lobbyFindItem(lobbyEquippedSlots?.main_hand),
    offHand: lobbyFindItem(lobbyEquippedSlots?.off_hand),
    armor: lobbyFindItem(lobbyEquippedSlots?.armor),
    wondrousItems: (lobbyEquippedSlots?.wondrous_items ?? [])
      .map(lobbyFindItem)
      .filter((v): v is SlotItem => v != null),
    twoHandedEquipped: lobbyEquippedSlots?.two_handed_equipped ?? false,
  };

  const lobbyInventoryDisplayItems: InventoryDisplayItem[] = groupInventoryItems(
    lobbyInventory.map((item) => ({
      name: item.name,
      emoji: lobbyItemEmojiMap.get(item.name.toLowerCase()),
      type: item.item_kind as string,
      quantity: item.quantity,
      equipped: lobbyEquippedItemIds.some((id) => id === item.item_id),
      bonus: item.attack_bonus ? `+${item.attack_bonus}` : undefined,
      damage: lobbyWeaponDamageMap.get(item.name.toLowerCase()) ?? item.damage,
      ac: item.base_ac ?? item.ac_bonus,
      description: item.description,
      is_magic:
        (item.attack_bonus ?? 0) > 0 ||
        (item.ac_bonus ?? 0) > 0 ||
        item.attunement === true ||
        (item.item_kind as string) === 'rare',
    }))
  );

  const spellcasting = permanent?.spellcasting;
  const rawPreparedLobby = (session?.prepared_spells as Array<unknown>) ?? [];
  const preparedFromLobby = rawPreparedLobby
    .map((s) =>
      typeof s === 'string' ? s : (((s as Record<string, unknown>)?.name as string) ?? '')
    )
    .filter(Boolean);
  const alwaysPreparedLobby = (
    (spellcasting?.always_prepared ?? []) as Array<string | { name: string }>
  ).map((s) => (typeof s === 'string' ? s : s.name));
  const lobbyPreparedMechanics =
    (permanent?.spell_mechanics as Array<Record<string, unknown>>) ?? [];
  const lobbyPreparedEmojiMap = new Map(
    lobbyPreparedMechanics
      .filter((s) => s.emoji && s.name)
      .map((s) => [(s.name as string).toLowerCase(), s.emoji as string])
  );
  const lobbyPreparedSummaryMap = new Map(
    lobbyPreparedMechanics
      .filter((s) => s.summary && s.name)
      .map((s) => [(s.name as string).toLowerCase(), s.summary as string])
  );
  // Union session-prepared + always-prepared, deduped by lowercase name.
  // Pre-fix this was a ternary that *picked* one source, so Cure Wounds (which
  // lives only in `always_prepared` for a Magic Initiate caster) was hidden
  // whenever the player had any session-prepared spells. Mirror the spellbook
  // helper's behaviour but flat-list for the prepared pill.
  const rawPreparedNames = (() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const name of [...preparedFromLobby, ...alwaysPreparedLobby]) {
      const key = name.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(name);
      }
    }
    return out;
  })();
  // Per SRD 5.2.1, always-prepared spells (feat / domain / subclass grants)
  // don't count against the prep cap. The cap badge counts session-prepared
  // entries that are NOT also always-prepared. For chars whose session list
  // already excludes always-prepared (post-2026-05-24 `build_prepared_spells_from_llm`
  // fix) the filter is a no-op; for legacy chars whose session list still
  // contains always-prepared, the filter drops them so the cap reads correctly.
  const alwaysPreparedLowerLobby = new Set(alwaysPreparedLobby.map((n) => n.toLowerCase()));
  const chosenPreparedCountLobby = preparedFromLobby.filter(
    (n) => !alwaysPreparedLowerLobby.has(n.toLowerCase())
  ).length;
  const preparedSpellNames = rawPreparedNames.map((name) => {
    const emoji = lobbyPreparedEmojiMap.get(name.toLowerCase());
    const summary = lobbyPreparedSummaryMap.get(name.toLowerCase());
    return emoji || summary ? { name, emoji, summary } : name;
  });
  const spellSlotsUsed = session?.spell_slots_used ?? {};
  const spellSlots: Array<{ level: number; available: number; max: number }> = [];
  if (spellcasting?.max_spell_slots) {
    for (const [level, max] of Object.entries(spellcasting.max_spell_slots)) {
      if (max && max > 0) {
        const levelNum = parseInt(level);
        const used = spellSlotsUsed[levelNum as keyof typeof spellSlotsUsed] ?? 0;
        spellSlots.push({ level: levelNum, available: max - used, max });
      }
    }
  }

  const rawFeats =
    (permanent?.feats as Array<string | { name: string; description?: string }>) ?? [];
  const feats: Array<{ name: string; emoji?: string; description?: string }> = rawFeats.map((f) =>
    typeof f === 'string' ? { name: f } : f
  );

  const traitsArray =
    permanent?.traits && permanent.traits.length > 0
      ? permanent.traits
      : (permanent?.species_traits ?? []);
  const features = [
    ...(permanent?.class_features?.map((f) => ({
      name: f.name,
      emoji: f.emoji,
      source: permanent?.class ?? 'Class',
      uses: f.max_uses ? `${f.max_uses} / ${(f.recharge ?? 'rest').replace(/_/g, ' ')}` : 'Passive',
      description: f.description,
    })) ?? []),
    ...(traitsArray.map((t) => ({
      name: t.name,
      emoji: (t as Record<string, unknown>).emoji as string | undefined,
      source: permanent?.species ?? 'Racial',
      uses: 'Passive',
      description: t.description,
    })) ?? []),
  ];

  // Spellbook list: merge `spells_known_by_level` + `always_prepared` (feat /
  // subclass / domain grants) so Magic Initiate Cure Wounds surfaces alongside
  // the class's known list. Case-insensitive dedupe handles Hunter's Mark,
  // which Rangers carry in BOTH sources.
  const allKnownSpells: SpellbookLevel[] = mergeSpellbookLevels(
    permanent?.spellcasting,
    permanent?.spell_mechanics
  );

  const hasVitals = vitalsData != null;
  const hasPortrait = true;
  const hasEquippedSlots = true;
  const hasPreparedContent =
    preparedSpellNames.length > 0 ||
    preparationCap(
      permanent?.class as string | undefined,
      (spellcasting as Record<string, unknown> | undefined)?.max_prepared_spells as
        | number
        | undefined
    ) != null;
  const hasFeaturesOrFeats = features.length > 0 || feats.length > 0;
  const hasInventory =
    lobbyInventoryDisplayItems.length > 0 || ((permanent?.gold_cp as number) ?? 0) > 0;
  const hasSpellbook = allKnownSpells.length > 0;

  const sections = [
    { key: 'vitals', hasContent: hasVitals },
    { key: 'portrait', hasContent: hasPortrait },
    { key: 'equipped', hasContent: hasEquippedSlots },
    { key: 'spellslots', hasContent: spellSlots.length > 0 },
    { key: 'prepared', hasContent: hasPreparedContent },
    { key: 'spellbook', hasContent: hasSpellbook },
    { key: 'features', hasContent: hasFeaturesOrFeats },
    { key: 'inventory', hasContent: hasInventory },
  ];
  const visibleSections = sections.filter((s) => s.hasContent);
  const needsSeparator = (key: string) => {
    const idx = visibleSections.findIndex((s) => s.key === key);
    return idx < visibleSections.length - 1;
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const avatarEmoji = permanent?.avatar_emoji || selectedCharacter.avatarEmoji;
  const lobbySubtitle = permanent
    ? `Level ${permanent.level ?? 1} ${permanent.species ?? ''} ${permanent.class ?? ''}`.trim()
    : undefined;

  return (
    <div
      className={
        isExpanded
          ? 'h-full flex flex-col cursor-default [&_button]:cursor-pointer'
          : 'flex flex-col self-start w-full cursor-default [&_button]:cursor-pointer'
      }
    >
      <HeaderCard
        characterName={characterName}
        subtitle={lobbySubtitle}
        avatar={selectedCharacter.avatar}
        avatarEmoji={avatarEmoji}
        portraitUrl={selectedCharacter.portraitUrl ?? effectivePortraitUrl ?? undefined}
        isExpanded={isExpanded}
        onToggleExpanded={toggleExpanded}
      >
        {isExpanded && (
          <>
            {vitalsData && (
              <VitalsSection
                data={vitalsData}
                expanded={expandedSection === 'vitals'}
                onToggle={() => toggleSection('vitals')}
                showSeparator={needsSeparator('vitals')}
              />
            )}

            {hasPortrait && (
              <PortraitSection
                portraitUrl={effectivePortraitUrl}
                portraitType={characterPortraitType || 'image'}
                characterName={characterName}
                expanded={expandedSection === 'portrait'}
                onToggle={() => toggleSection('portrait')}
                showSeparator={needsSeparator('portrait')}
                isGenerating={isGeneratingPortrait}
              />
            )}

            {hasEquippedSlots && (
              <EquippedSection
                slots={lobbySlotsData}
                expanded={expandedSection === 'equipped'}
                onToggle={() => toggleSection('equipped')}
                showSeparator={needsSeparator('equipped')}
              />
            )}

            {spellSlots.length > 0 && (
              <SpellSlotsSection
                expanded={expandedSection === 'spellslots'}
                onToggle={() => toggleSection('spellslots')}
                spellSlots={spellSlots}
                showSeparator={needsSeparator('spellslots')}
              />
            )}

            {hasPreparedContent && (
              <PreparedSection
                expanded={expandedSection === 'prepared'}
                onToggle={() => toggleSection('prepared')}
                preparedSpellNames={preparedSpellNames}
                chosenPreparedCount={chosenPreparedCountLobby}
                maxPreparedSpells={preparationCap(
                  permanent?.class as string | undefined,
                  (spellcasting as Record<string, unknown> | undefined)?.max_prepared_spells as
                    | number
                    | undefined
                )}
                showSeparator={needsSeparator('prepared')}
              />
            )}

            {hasSpellbook && (
              <SpellbookSection
                expanded={expandedSection === 'spellbook'}
                onToggle={() => toggleSection('spellbook')}
                allKnownSpells={allKnownSpells}
                showSeparator={needsSeparator('spellbook')}
              />
            )}

            {hasFeaturesOrFeats && (
              <FeaturesSection
                expanded={expandedSection === 'features'}
                onToggle={() => toggleSection('features')}
                feats={feats}
                features={features}
                showSeparator={needsSeparator('features')}
              />
            )}

            {hasInventory && (
              <InventorySection
                expanded={expandedSection === 'inventory'}
                onToggle={() => toggleSection('inventory')}
                items={lobbyInventoryDisplayItems}
                goldCp={
                  ((permanent?.gold_cp as number) ?? 0) > 0
                    ? (permanent!.gold_cp as number)
                    : undefined
                }
                showSeparator={needsSeparator('inventory')}
              />
            )}
          </>
        )}
      </HeaderCard>
    </div>
  );
}
